import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import axiosRetry from "axios-retry";

import { store } from "@/redux";

const withAuthHeader = (): AxiosRequestConfig["headers"] => {
  const { authSlice } = store.getState();
  return {
    Authorization: authSlice.jwtToken,
  };
};

export const axiosClient = axios.create({
  baseURL: process.env.REACT_APP_BASE_TASK_API_URL,
  headers: {
    ...axios.defaults.headers,
    "Content-Type": "application/json",
  },
});

// Exponential back-off retry delay between requests
// https://developers.google.com/analytics/devguides/reporting/core/v3/errors#backoff
axiosRetry(axiosClient, {
  retries: 3,
  retryDelay: (retryCount) => retryCount * 1000,
  retryCondition: (error) => {
    if (error?.response?.status === 401) {
      // logout user when status code is 401 (unauthorized)
      localStorage.clear();
      window.location.reload();
    }
    return !error?.response;
  },
});

export const get = <T>(
  path: string,
  params: Record<string, unknown> = {},
  config: AxiosRequestConfig = {},
): Promise<AxiosResponse<T>> =>
    axiosClient.get(path, {
      params,
      ...config,
      headers: {
        ...withAuthHeader(),
        ...config.headers,
      },
    });

export const post = <T>(
  path: string,
  data: unknown = {},
  config: AxiosRequestConfig = {},
): Promise<AxiosResponse<T>> => {
  return axiosClient.post(path, data, {
    ...config,
    headers: {
      ...withAuthHeader(),
      ...config.headers,
    },
  });
};

export const put = <T>(
  path: string,
  data: unknown = {},
  config: AxiosRequestConfig = {},
): Promise<AxiosResponse<T>> =>
    axiosClient.put(path, data, {
      ...config,
      headers: {
        ...withAuthHeader(),
        ...config.headers,
      },
    });

export const del = <T>(
  path: string,
  params?: Record<string, unknown>,
): Promise<AxiosResponse<T>> => {
  return axiosClient.delete(path, {
    params,
    headers: withAuthHeader(),
  });
};
